import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Button, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  TableContainer, Paper, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, IconButton, Tooltip, Tabs, Tab, Divider,
  CircularProgress, FormControlLabel, Checkbox, List, ListItem, ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useSearchParams } from 'react-router-dom';
import { projectService, leadService, pressFormService } from '../services/api';

const STATUSES = [
  { value: 'modeling', label: '3Д моделювання', color: 'info' },
  { value: 'model_approval', label: 'Погодження моделі', color: 'secondary' },
  { value: 'press_form_design', label: 'Проектування ПФ', color: 'warning' },
  { value: 'press_form_manufacturing', label: 'Виготовлення ПФ', color: 'warning' },
  { value: 'testing', label: 'Тестування', color: 'primary' },
  { value: 'completed', label: 'Завершено', color: 'success' },
];

const MODEL3D_STATUSES = [
  { value: 'client_ready', label: 'Готова у клієнта' },
  { value: 'refinement', label: 'Доопрацювання' },
  { value: 'from_scratch', label: 'Розробка з нуля' },
  { value: 'approved', label: 'Погоджено' },
];

const STEP_STATUSES = [
  { value: 'pending', label: 'Очікує' },
  { value: 'in_progress', label: 'В роботі' },
  { value: 'done', label: 'Завершено' },
];

const TEST_STATUSES = [
  { value: 'pending', label: 'Очікує' },
  { value: 'in_progress', label: 'В роботі' },
  { value: 'passed', label: 'Пройдено' },
  { value: 'failed', label: 'Не пройдено' },
];

const statusColor = (s) => STATUSES.find((x) => x.value === s)?.color || 'default';
const statusLabel = (s) => STATUSES.find((x) => x.value === s)?.label || s;

function TabPanel({ children, value, index }) {
  return value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null;
}

function ProjectDetailDialog({ project, open, onClose, onSaved }) {
  const [tab, setTab] = useState(0);
  const [model3d, setModel3d] = useState(project?.model_3d || {});
  const [pfProc, setPfProc] = useState(project?.press_form_process || {});
  const [payForm, setPayForm] = useState({ direction: 'income', payment_type: 'press_form', amount: '', date: new Date().toISOString().slice(0, 10), is_paid: false, notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (project) {
      setModel3d(project.model_3d || {});
      setPfProc(project.press_form_process || {});
    }
  }, [project]);

  if (!project) return null;

  const saveModel3d = async () => {
    setSaving(true);
    try { await projectService.updateModel3D(project.id, model3d); onSaved(); }
    finally { setSaving(false); }
  };

  const savePfProc = async () => {
    setSaving(true);
    try { await projectService.updatePressFormProcess(project.id, pfProc); onSaved(); }
    finally { setSaving(false); }
  };

  const addPayment = async () => {
    if (!payForm.amount) return;
    setSaving(true);
    try { await projectService.addPayment(project.id, payForm); setPayForm({ ...payForm, amount: '', notes: '' }); onSaved(); }
    finally { setSaving(false); }
  };

  const deletePayment = async (payId) => {
    await projectService.deletePayment(project.id, payId);
    onSaved();
  };

  const income = (project.payments || []).filter((p) => p.direction === 'income');
  const expense = (project.payments || []).filter((p) => p.direction === 'expense');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Проект: {project.name} <Chip label={statusLabel(project.status)} color={statusColor(project.status)} size="small" sx={{ ml: 1 }} /></DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          <Tab label="3Д модель" />
          <Tab label="Прес-форма" />
          <Tab label="Платежі" />
        </Tabs>

        <TabPanel value={tab} index={0}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField select label="Статус моделі" value={model3d.status || 'from_scratch'} onChange={(e) => setModel3d({ ...model3d, status: e.target.value })} fullWidth>
              {MODEL3D_STATUSES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
            </TextField>
            <TextField label="Виконавець (якщо зовнішній)" value={model3d.contractor_name || ''} onChange={(e) => setModel3d({ ...model3d, contractor_name: e.target.value })} fullWidth />
            <TextField label="Ціна для ліда (грн)" type="number" value={model3d.price_for_lead || ''} onChange={(e) => setModel3d({ ...model3d, price_for_lead: e.target.value })} fullWidth />
            <TextField label="Нотатки" value={model3d.notes || ''} onChange={(e) => setModel3d({ ...model3d, notes: e.target.value })} fullWidth multiline rows={3} />
            <Button variant="contained" onClick={saveModel3d} disabled={saving}>Зберегти</Button>
          </Box>
        </TabPanel>

        <TabPanel value={tab} index={1}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Підрядник" value={pfProc.contractor_name || ''} onChange={(e) => setPfProc({ ...pfProc, contractor_name: e.target.value })} fullWidth />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField label="Вартість підряднику (грн)" type="number" value={pfProc.contractor_price || ''} onChange={(e) => setPfProc({ ...pfProc, contractor_price: e.target.value })} fullWidth />
              <TextField label="Ціна для ліда (грн)" type="number" value={pfProc.price_for_lead || ''} onChange={(e) => setPfProc({ ...pfProc, price_for_lead: e.target.value })} fullWidth />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField select label="Проектування" value={pfProc.design_status || 'pending'} onChange={(e) => setPfProc({ ...pfProc, design_status: e.target.value })} fullWidth>
                {STEP_STATUSES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
              </TextField>
              <TextField select label="Виготовлення" value={pfProc.manufacturing_status || 'pending'} onChange={(e) => setPfProc({ ...pfProc, manufacturing_status: e.target.value })} fullWidth>
                {STEP_STATUSES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
              </TextField>
              <TextField select label="Тестування" value={pfProc.testing_status || 'pending'} onChange={(e) => setPfProc({ ...pfProc, testing_status: e.target.value })} fullWidth>
                {TEST_STATUSES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
              </TextField>
            </Box>
            <TextField label="Нотатки" value={pfProc.notes || ''} onChange={(e) => setPfProc({ ...pfProc, notes: e.target.value })} fullWidth multiline rows={3} />
            <Button variant="contained" onClick={savePfProc} disabled={saving}>Зберегти</Button>
          </Box>
        </TabPanel>

        <TabPanel value={tab} index={2}>
          <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
            <Paper sx={{ p: 1.5, flex: 1, minWidth: 150 }}>
              <Typography variant="caption">Отримано від ліда</Typography>
              <Typography variant="h6" color="success.main">{income.filter((p) => p.is_paid).reduce((s, p) => s + parseFloat(p.amount), 0).toLocaleString()} грн</Typography>
            </Paper>
            <Paper sx={{ p: 1.5, flex: 1, minWidth: 150 }}>
              <Typography variant="caption">Сплачено підряднику</Typography>
              <Typography variant="h6" color="error.main">{expense.filter((p) => p.is_paid).reduce((s, p) => s + parseFloat(p.amount), 0).toLocaleString()} грн</Typography>
            </Paper>
          </Box>

          <Typography variant="subtitle2" gutterBottom>Додати платіж</Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
            <TextField select label="Напрямок" value={payForm.direction} onChange={(e) => setPayForm({ ...payForm, direction: e.target.value })} sx={{ width: 150 }}>
              <MenuItem value="income">Від ліда</MenuItem>
              <MenuItem value="expense">Підряднику</MenuItem>
            </TextField>
            <TextField select label="Тип" value={payForm.payment_type} onChange={(e) => setPayForm({ ...payForm, payment_type: e.target.value })} sx={{ width: 150 }}>
              <MenuItem value="model_3d">3Д модель</MenuItem>
              <MenuItem value="press_form">Прес-форма</MenuItem>
              <MenuItem value="other">Інше</MenuItem>
            </TextField>
            <TextField label="Сума (грн)" type="number" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} sx={{ width: 120 }} />
            <TextField label="Дата" type="date" value={payForm.date} onChange={(e) => setPayForm({ ...payForm, date: e.target.value })} sx={{ width: 150 }} InputLabelProps={{ shrink: true }} />
            <TextField label="Примітка" value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} sx={{ width: 180 }} />
            <FormControlLabel control={<Checkbox checked={payForm.is_paid} onChange={(e) => setPayForm({ ...payForm, is_paid: e.target.checked })} />} label="Оплачено" />
            <Button variant="contained" onClick={addPayment} disabled={!payForm.amount || saving}>Додати</Button>
          </Box>

          <Divider sx={{ mb: 1 }} />
          <List dense>
            {(project.payments || []).map((p) => (
              <ListItem key={p.id} disableGutters>
                <ListItemText
                  primary={`${p.direction === 'income' ? '↓ Від ліда' : '↑ Підряднику'} · ${p.payment_type_display} · ${parseFloat(p.amount).toLocaleString()} грн`}
                  secondary={`${p.date}${p.notes ? ' · ' + p.notes : ''}${p.is_paid ? '' : ' · Не оплачено'}`}
                />
                <Chip size="small" label={p.is_paid ? 'Оплачено' : 'Очікує'} color={p.is_paid ? 'success' : 'default'} sx={{ mr: 1 }} />
                <ListItemSecondaryAction>
                  <IconButton size="small" color="error" onClick={() => deletePayment(p.id)}><DeleteIcon fontSize="small" /></IconButton>
                </ListItemSecondaryAction>
              </ListItem>
            ))}
          </List>
        </TabPanel>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Закрити</Button>
      </DialogActions>
    </Dialog>
  );
}

const EMPTY_PROJ = { lead: '', name: '', status: 'modeling', notes: '' };

export default function ProjectsPage() {
  const [searchParams] = useSearchParams();
  const leadFilter = searchParams.get('lead') || '';

  const [projects, setProjects] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_PROJ, lead: leadFilter });
  const [editId, setEditId] = useState(null);
  const [detailProject, setDetailProject] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (leadFilter) params.lead = leadFilter;
      if (statusFilter) params.status = statusFilter;
      const [projRes, leadsRes] = await Promise.all([projectService.getAll(params), leadService.getAll()]);
      setProjects(projRes.data);
      setLeads(leadsRes.data);
    } finally {
      setLoading(false);
    }
  }, [leadFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    if (editId) { await projectService.update(editId, form); }
    else { await projectService.create(form); }
    setDialogOpen(false);
    setForm({ ...EMPTY_PROJ, lead: leadFilter });
    setEditId(null);
    load();
  };

  const handleEdit = (p) => {
    setForm({ lead: p.lead, name: p.name, status: p.status, notes: p.notes });
    setEditId(p.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Видалити проект?')) return;
    await projectService.remove(id);
    load();
  };

  const openDetail = async (proj) => {
    const res = await projectService.getById(proj.id);
    setDetailProject(res.data);
  };

  const handleDetailSaved = async () => {
    const res = await projectService.getById(detailProject.id);
    setDetailProject(res.data);
    load();
  };

  const leadName = (id) => leads.find((l) => l.id === id)?.name || '—';

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5">Проекти{leadFilter ? ` · ${leadName(parseInt(leadFilter))}` : ''}</Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <TextField select size="small" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} sx={{ width: 200 }} label="Статус">
            <MenuItem value="">Всі</MenuItem>
            {STATUSES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
          </TextField>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => { setForm({ ...EMPTY_PROJ, lead: leadFilter }); setEditId(null); setDialogOpen(true); }}>
            Новий проект
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Назва</TableCell>
              <TableCell>Лід</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>3Д модель</TableCell>
              <TableCell>Прес-форма</TableCell>
              <TableCell>Баланс платежів</TableCell>
              <TableCell>Дата</TableCell>
              <TableCell>Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} align="center"><CircularProgress size={24} /></TableCell></TableRow>
            ) : projects.length === 0 ? (
              <TableRow><TableCell colSpan={8} align="center">Немає проектів</TableCell></TableRow>
            ) : projects.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell><strong>{p.name}</strong></TableCell>
                <TableCell>{p.lead_name}</TableCell>
                <TableCell><Chip label={statusLabel(p.status)} color={statusColor(p.status)} size="small" /></TableCell>
                <TableCell>
                  {p.model_3d ? <Chip size="small" label={MODEL3D_STATUSES.find((s) => s.value === p.model_3d.status)?.label || p.model_3d.status} variant="outlined" /> : '—'}
                </TableCell>
                <TableCell>
                  {p.press_form_process ? (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <Chip size="small" label={`П: ${STEP_STATUSES.find((s) => s.value === p.press_form_process.design_status)?.label}`} variant="outlined" />
                      <Chip size="small" label={`В: ${STEP_STATUSES.find((s) => s.value === p.press_form_process.manufacturing_status)?.label}`} variant="outlined" />
                      <Chip size="small" label={`Т: ${TEST_STATUSES.find((s) => s.value === p.press_form_process.testing_status)?.label}`} variant="outlined" />
                    </Box>
                  ) : '—'}
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="success.main">+{(p.total_income || 0).toLocaleString()}</Typography>
                  {' / '}
                  <Typography variant="caption" color="error.main">-{(p.total_expense || 0).toLocaleString()}</Typography>
                </TableCell>
                <TableCell>{new Date(p.created_at).toLocaleDateString('uk-UA')}</TableCell>
                <TableCell>
                  <Tooltip title="Деталі"><IconButton size="small" onClick={() => openDetail(p)}><VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                  <IconButton size="small" onClick={() => handleEdit(p)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(p.id)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editId ? 'Редагувати проект' : 'Новий проект'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          <TextField select label="Лід *" value={form.lead} onChange={(e) => setForm({ ...form, lead: e.target.value })} fullWidth>
            {leads.map((l) => <MenuItem key={l.id} value={l.id}>{l.name}</MenuItem>)}
          </TextField>
          <TextField label="Назва проекту *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} fullWidth />
          <TextField select label="Статус" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} fullWidth>
            {STATUSES.map((s) => <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>)}
          </TextField>
          <TextField label="Нотатки" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} fullWidth multiline rows={3} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Скасувати</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name.trim() || !form.lead}>Зберегти</Button>
        </DialogActions>
      </Dialog>

      <ProjectDetailDialog project={detailProject} open={!!detailProject} onClose={() => setDetailProject(null)} onSaved={handleDetailSaved} />
    </Box>
  );
}
